/**
 * TourController — headless component that syncs tour steps with the current route.
 *
 * Lives inside the TourProvider (from @reactour/tour) and the Router tree.
 * Runs a useEffect on pathname change to update steps and close the active tour.
 *
 * Uses a ref to avoid the React.StrictMode double-invocation issue.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import { useAuth } from '@/context/AuthContext';
import { useTourContext } from '@/context/TourContext';
import { getTourStepsForRoute } from '@/tours';
import type { TourStepContext } from '@/tours/types';

/** Breakpoint matching Layout.tsx's mobile detection */
const MOBILE_BREAKPOINT = 768;

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

export function TourController() {
  const location = useLocation();
  const { setSteps, setCurrentStep, setIsOpen, isOpen } = useTour();
  const { user, hasPermission, hasFeature } = useAuth();
  const { markSeen } = useTourContext();
  const lastPathnameRef = useRef<string | null>(null);
  const openedForPathnameRef = useRef<string | null>(null);

  // Sync steps when route changes
  useEffect(() => {
    // Prevent double execution from React.StrictMode
    if (lastPathnameRef.current === location.pathname) return;
    lastPathnameRef.current = location.pathname;

    const ctx: TourStepContext = {
      hasPermission: hasPermission as (permission: string) => boolean,
      hasFeature,
      isMobile: isMobileViewport(),
      // Superadmin without a tenant_id: assume all features available
      isSuperadmin: user?.is_superadmin ?? false,
    };

    const steps = getTourStepsForRoute(location.pathname, ctx);

    if (setSteps) setSteps(steps);
    setCurrentStep(0);
    setIsOpen(false);
  }, [location.pathname, hasPermission, hasFeature, user?.is_superadmin, setSteps, setCurrentStep, setIsOpen]);

  // Mark tour as seen when it is opened
  useEffect(() => {
    if (isOpen && openedForPathnameRef.current !== location.pathname) {
      openedForPathnameRef.current = location.pathname;
      markSeen(location.pathname);
    }
    if (!isOpen && openedForPathnameRef.current === location.pathname) {
      openedForPathnameRef.current = null;
    }
  }, [isOpen, location.pathname, markSeen]);

  return null;
}
